import { useEffect, useState } from "react";
import api, { apiErr, fmtMoney, fmtDate } from "@/lib/api";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import EntityForm from "@/components/EntityForm";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, Building2, MoreVertical, Pencil, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Immobilisations() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/accounting/assets").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const schema = [
    { name: "libelle", label: "Désignation", required: true, col: 2 },
    { name: "valeur", label: "Valeur d'acquisition (FCFA)", type: "number", required: true },
    { name: "date_acquisition", label: "Date d'acquisition", type: "date", required: true },
    { name: "duree", label: "Durée d'amortissement (années)", type: "number", required: true },
    { name: "compte_immo", label: "Compte immobilisation", placeholder: "218300" },
    { name: "compte_amort", label: "Compte amortissement", placeholder: "281800" },
  ];

  const save = async (form) => {
    const payload = { ...form, valeur: Number(form.valeur) || 0, duree: Number(form.duree) || 1, compte_immo: form.compte_immo || "218300", compte_amort: form.compte_amort || "281800" };
    try {
      if (editing) await api.put(`/accounting/assets/${editing.id}`, payload);
      else await api.post("/accounting/assets", payload);
      toast.success(editing ? "Immobilisation mise à jour" : "Immobilisation créée"); load();
    } catch (e) { toast.error(apiErr(e)); throw e; }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer cette immobilisation ?")) return; await api.delete(`/accounting/assets/${id}`); toast.success("Supprimée"); load(); };
  const depreciate = async (a) => {
    try { await api.post(`/accounting/assets/${a.id}/depreciate`); toast.success("Dotation d'amortissement générée (brouillon)"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <PageHeader title="Immobilisations" subtitle="Suivi des immobilisations & amortissements linéaires">
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-asset" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouvelle immobilisation</Button>
      </PageHeader>
      {items === null ? <Loader /> : items.length === 0 ? (
        <EmptyState icon={Building2} title="Aucune immobilisation" subtitle="Enregistrez vos biens durables pour suivre les amortissements." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>N°</TableHead><TableHead>Désignation</TableHead><TableHead>Acquisition</TableHead><TableHead className="text-right">Valeur</TableHead><TableHead className="text-right">Dotation/an</TableHead><TableHead className="text-right">Cumul amort.</TableHead><TableHead className="text-right">VNC</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((a) => (
                <TableRow key={a.id} data-testid={`asset-row-${a.id}`} className="hover:bg-slate-50">
                  <TableCell className="font-mono text-xs text-slate-500">{a.numero}</TableCell>
                  <TableCell className="font-semibold">{a.libelle}</TableCell>
                  <TableCell className="font-mono text-xs">{fmtDate(a.date_acquisition)} · {a.duree} ans</TableCell>
                  <TableCell className="text-right font-mono text-xs">{fmtMoney(a.valeur)}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{fmtMoney(a.dotation_annuelle)}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{fmtMoney(a.cumul_amortissement)}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-bold text-brand-700">{fmtMoney(a.vnc)}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`asset-menu-${a.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => depreciate(a)} data-testid={`asset-depreciate-${a.id}`}><Sparkles className="h-4 w-4 mr-2" />Générer la dotation</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setEditing(a); setOpen(true); }}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => remove(a.id)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}
      <EntityForm open={open} onOpenChange={setOpen} title={editing ? "Modifier l'immobilisation" : "Nouvelle immobilisation"} schema={schema} initial={editing || { duree: 5, compte_immo: "218300", compte_amort: "281800" }} onSubmit={save} />
    </div>
  );
}
